/**
 * SystemHealth.tsx — Component health dashboard for super_admin.
 */

import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ErrorIcon from '@mui/icons-material/Error';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import axios from 'axios';
import API_BASE from '../../config/api';

// ── Component icons ───────────────────────────────────────────────────────────

const COMPONENT_ICONS: Record<string, React.ReactNode> = {
  database:          <StorageIcon />,
  snap_collector:    <TravelExploreIcon />,
  shadow_ai_monitor: <VisibilityIcon />,
  autofix_engine:    <AutoFixHighIcon />,
  ticket_system:     <ConfirmationNumberIcon />,
};

// ── Status helpers ────────────────────────────────────────────────────────────

function statusColor(status: string) {
  if (status === 'healthy')  return '#2ecc71';
  if (status === 'degraded') return '#f77f00';
  return '#ef233c';
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'healthy')
    return <CheckCircleIcon sx={{ color: '#2ecc71' }} />;
  if (status === 'degraded')
    return <WarningAmberIcon sx={{ color: '#f77f00' }} />;
  return <ErrorIcon sx={{ color: 'error.main' }} />;
}

// ── Component card ────────────────────────────────────────────────────────────

function ComponentCard({ name, data }: { name: string; data: Record<string, unknown> }) {
  const status = (data.status as string) ?? 'unknown';
  const details = Object.entries(data).filter(([k]) => k !== 'status');

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: `1px solid ${statusColor(status)}33`,
        bgcolor: status === 'healthy'
          ? 'rgba(46,204,113,0.04)'
          : status === 'degraded'
          ? 'rgba(247,127,0,0.06)'
          : 'rgba(239,35,60,0.06)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ color: statusColor(status), display: 'flex' }}>
            {COMPONENT_ICONS[name] ?? <HealthAndSafetyIcon />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
              {name.replace(/_/g, ' ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusIcon status={status} />
            <Chip
              label={status}
              size="small"
              sx={{
                bgcolor: `${statusColor(status)}22`,
                color: statusColor(status),
                textTransform: 'capitalize',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {details.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No additional details.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {details.map(([key, val]) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {key.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: '60%', textAlign: 'right' }}>
                  {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SystemHealth() {
  const [health, setHealth]   = useState<Record<string, Record<string, unknown>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  async function fetchHealth() {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_BASE}/admin/health`);
      // Normalise: each value should be an object with at least { status }
      const normalised: Record<string, Record<string, unknown>> = {};
      const components = data.components ?? data;
      for (const [key, val] of Object.entries(components)) {
        normalised[key] = typeof val === 'object' && val !== null
          ? (val as Record<string, unknown>)
          : { status: String(val) };
      }
      setHealth(normalised);
      setLastRefresh(new Date());
    } catch {
      setError('Failed to load health data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchHealth(); }, []);

  const allHealthy  = Object.values(health).every((c) => c.status === 'healthy');
  const anyUnhealthy = Object.values(health).some((c) => c.status !== 'healthy' && c.status !== 'degraded');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthAndSafetyIcon color="primary" /> System Health
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastRefresh && (
            <Typography variant="caption" color="text.secondary">
              Last refreshed: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchHealth}
              disabled={loading}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && Object.keys(health).length > 0 && (
        <Alert
          severity={anyUnhealthy ? 'error' : allHealthy ? 'success' : 'warning'}
          sx={{ mb: 3 }}
        >
          {anyUnhealthy
            ? 'One or more components are unhealthy. Immediate attention required.'
            : allHealthy
            ? 'All systems are operating normally.'
            : 'Some components are degraded. Monitoring recommended.'}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {Object.entries(health).map(([name, data]) => (
            <Grid item xs={12} sm={6} md={4} key={name}>
              <ComponentCard name={name} data={data} />
            </Grid>
          ))}
          {Object.keys(health).length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" textAlign="center" py={4}>
                No health data available.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
